import React, { useMemo, useRef, useState } from 'react'
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser'
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import RemoveCircleOutlineIcon from '@material-ui/icons/RemoveCircleOutline'
import HelpOutlineIcon from '@material-ui/icons/HelpOutline'
import { Badge, Section } from '../../Bgv/BusinessDetailReport/BusinessDetailReport'
import shared from '../../Bgv/BusinessDetailReport/BusinessDetailReport.module.scss'
import styles from './VerificationReport.module.scss'
import { buildVerificationReport, scoreText } from './verificationReportModel'
import { SCORE_WORDS } from './verificationCodes'
import { withReportErrorBoundary } from '../../Bgv/BusinessDetailReport/ReportErrorBoundary'

const cx = (...names) => names.filter(Boolean).join(' ')
const badgeTone = (tone) => (tone === 'notSent' ? 'neutral' : tone)
const TAG = { declined: 'Red', watch: 'Amber', neutral: 'Info', notSent: 'Not sent', approved: 'OK' }

const DIAL = { cx: 110, cy: 110, R: 104, r: 72, gap: 3 }
const dialPoint = (radius, angle) => [DIAL.cx + radius * Math.cos(Math.PI * (180 - angle) / 180), DIAL.cy - radius * Math.sin(Math.PI * (180 - angle) / 180)]
const dialSegments = () => {
    const span = (180 - DIAL.gap * 4) / 5
    return [0, 1, 2, 3, 4].map(i => {
        const a0 = i * (span + DIAL.gap)
        const a1 = a0 + span
        const [x0, y0] = dialPoint(DIAL.R, a0)
        const [x1, y1] = dialPoint(DIAL.R, a1)
        const [x2, y2] = dialPoint(DIAL.r, a1)
        const [x3, y3] = dialPoint(DIAL.r, a0)
        const f = n => n.toFixed(1)
        return `M${f(x0)} ${f(y0)} A${DIAL.R} ${DIAL.R} 0 0 1 ${f(x1)} ${f(y1)} L${f(x2)} ${f(y2)} A${DIAL.r} ${DIAL.r} 0 0 0 ${f(x3)} ${f(y3)} Z`
    })
}
const SEGMENTS = dialSegments()

export const ScoreDial = ({ score, tone }) => (
    <div className={styles.dial}>
        <div className={styles.dialLabel}>Lexis business score</div>
        <svg viewBox='0 0 220 124' className={styles.dialSvg} role='img' aria-label={scoreText(score)}>
            {SEGMENTS.map((d, i) => <path key={i} d={d} className={cx(styles.dialSeg, score != null && score >= (i + 1) * 10 && styles[`on_${tone}`])} />)}
            <text x='110' y='96' textAnchor='middle' className={cx(styles.dialNumber, styles[`ink_${tone}`])}>{score ?? '-'}</text>
            <text x='110' y='118' textAnchor='middle' className={styles.dialOf}>of 50</text>
        </svg>
        <div className={cx(styles.dialWords, styles[`ink_${tone}`])}>{score == null ? 'No score' : SCORE_WORDS[score]}</div>
    </div>
)

export const ScoreBar = ({ score, tone, capped, size = 'md', dark }) => (
    <span className={cx(styles.bar, styles[`bar_${size}`], dark && styles.barDark)} role='img' aria-label={scoreText(score)}>
        <span className={styles.barBlocks}>
            {[1, 2, 3, 4, 5].map(i => (
                <span key={i} className={cx(styles.block, score != null && score >= i * 10 ? styles[`on_${tone}`] : capped && i === 5 && styles.blockCapped)} />
            ))}
        </span>
        {capped && size !== 'xs' && <span className={styles.needsSsn}>Needs SSN</span>}
    </span>
)

const NoteList = ({ groups }) => (
    <div className={styles.notes}>
        {groups.map(g => (
            <div key={g.group} className={styles.noteGroup}>
                <div className={styles.noteGroupTitle}>{g.group}</div>
                <div className={styles.noteItems}>
                    {g.items.map((n, i) => (
                        <div key={`${n.code}-${i}`} className={styles.noteRow}>
                            <Badge tone={badgeTone(n.tone)}>{TAG[n.tone]}</Badge>
                            <span>{n.label}</span>
                        </div>
                    ))}
                </div>
            </div>
        ))}
    </div>
)

const More = ({ items }) => {
    const [open, setOpen] = useState(false)
    if (!items.length) return null
    return (
        <div className={styles.more}>
            <button type='button' className={shared.textButton} onClick={() => setOpen(v => !v)} aria-expanded={open}>
                {open ? 'Hide more from Lexis' : 'More from Lexis'}
            </button>
            {open && (
                <dl className={shared.uccDetails}>
                    {items.map(d => <React.Fragment key={d.label}><dt>{d.label}</dt><dd>{d.value}</dd></React.Fragment>)}
                </dl>
            )}
        </div>
    )
}

const Block = ({ title, children }) => (
    <div className={styles.group}>
        <div className={styles.groupTitle}>{title}</div>
        {children}
    </div>
)

const DETAIL_ICON = {
    verified: <CheckCircleIcon className={styles.iconOk} fontSize='small' />,
    notVerified: <HelpOutlineIcon className={styles.iconWatch} fontSize='small' />,
    notSent: <RemoveCircleOutlineIcon className={styles.iconGrey} fontSize='small' />,
}
const DETAIL_TEXT = { verified: 'Verified', notVerified: 'Not verified', notSent: 'Not sent' }

const WatchlistHits = ({ hits }) => (
    <div className={shared.uccList}>
        <div className={shared.note}>Lexis matches the OFAC list by name. Follow your internal policy on possible OFAC matches.</div>
        {hits.map((h, i) => (
            <div key={i} className={styles.hit}>
                <div className={styles.hitHead}>
                    <span className={styles.hitTitle}>{h.title}</span>
                    <Badge tone='declined' dot>{h.who === 'Business' ? 'Business match' : `Owner match: ${h.who}`}</Badge>
                </div>
                <dl className={shared.uccDetails}>
                    {[['List', h.list], ['Program', h.program], ['Country', h.country], ['Listed address', h.address]].filter(([, v]) => v)
                        .map(([label, value]) => <React.Fragment key={label}><dt>{label}</dt><dd>{value}</dd></React.Fragment>)}
                </dl>
            </div>
        ))}
    </div>
)

const BusinessCheck = ({ business }) => (
    <div className={styles.cardStack}>
        <Block title='What Lexis verified'>
            <div className={styles.details}>
                {business.details.map(d => (
                    <div key={d.label} className={styles.detail}>
                        {DETAIL_ICON[d.status]}
                        <span className={styles.detailLabel}>{d.label}</span>
                        <span className={cx(styles.detailStatus, styles[`status_${d.status}`])}>{DETAIL_TEXT[d.status]}</span>
                        <span className={styles.detailValue}>{d.value}</span>
                    </div>
                ))}
            </div>
        </Block>
        {business.compare.length > 0 && (
            <Block title='What you sent and what Lexis has'>
                <div className={shared.tableWrap}>
                    <table className={shared.table}>
                        <thead><tr><th /><th>Sent</th><th>Lexis has</th></tr></thead>
                        <tbody>
                            {business.compare.map(r => (
                                <tr key={r.label}>
                                    <td className={styles.compareLabel}>{r.label}</td>
                                    <td>{r.sent}</td>
                                    <td>{r.lexis}{r.differs && <> <Badge tone='watch'>Different</Badge></>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Block>
        )}
        {business.notes.length > 0 && <Block title='Risk notes'><NoteList groups={business.notes} /></Block>}
        {business.sources.length > 0 && (
            <Block title='Where Lexis looked'>
                <div className={styles.sources}>
                    {business.sources.map(s => (
                        <div key={s.label} className={styles.sourceRow}>
                            <span className={styles.detailLabel}>{s.label}</span>
                            <span className={s.count ? undefined : styles.muted}>{s.confirmed}</span>
                        </div>
                    ))}
                </div>
            </Block>
        )}
        <More items={business.more} />
    </div>
)

const OwnerCard = ({ owner }) => (
    <div className={styles.cardStack}>
        {owner.flags.length > 0 && (
            <div className={shared.inlineBadges}>
                {owner.flags.map(f => <Badge key={f.label} tone={badgeTone(f.tone)} dot={f.tone === 'declined'}>{f.label}</Badge>)}
            </div>
        )}
        {owner.link && (
            <Block title='Link to the business'>
                <Badge tone={badgeTone(owner.link.tone)} dot>{owner.link.text}</Badge>
            </Block>
        )}
        <Block title='Match results'>
            <div className={styles.sources}>
                {owner.matches.map(m => (
                    <div key={m.label} className={styles.sourceRow}>
                        <span className={styles.detailLabel}>{m.label}</span>
                        <span className={cx(styles.matchText, styles[`ink_${m.tone}`])}>{m.text}{m.source ? <span className={styles.muted}> · checked against {m.source}</span> : null}</span>
                    </div>
                ))}
            </div>
        </Block>
        {owner.notes.length > 0 && <Block title='Risk notes'><NoteList groups={owner.notes} /></Block>}
        {owner.followUps.length > 0 && (
            <Block title='Lexis follow-up actions'>
                <ol className={styles.followUps}>{owner.followUps.map(f => <li key={f}>{f}</li>)}</ol>
            </Block>
        )}
        {owner.history.length > 0 && (
            <Block title='Address history'>
                <div className={shared.tableWrap}>
                    <table className={shared.table}>
                        <thead><tr><th>Address</th><th>First seen</th><th>Last seen</th><th /></tr></thead>
                        <tbody>
                            {owner.history.map((h, i) => (
                                <tr key={i}>
                                    <td>{h.address}</td>
                                    <td className={shared.noWrap}>{h.firstSeen || '-'}</td>
                                    <td className={shared.noWrap}>{h.lastSeen || '-'}</td>
                                    <td>{h.best && <Badge tone='approved'>Best address</Badge>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </Block>
        )}
        <More items={owner.more} />
    </div>
)

const TILE_CLASS = { declined: shared.tileFlag, watch: styles.tileWatch }

function VerificationReport({ result }) {
    const model = useMemo(() => buildVerificationReport(result), [result])
    const idPrefix = useRef(`verification-${Math.random().toString(36).slice(2)}`).current
    const [openState, setOpenState] = useState({})

    if (!model.hasData) {
        return (
            <div className={shared.body}>
                <div className={shared.calmLine}>Lexis returned no verification result for this business.</div>
            </div>
        )
    }

    const { business, owners, hits, tiles, verdict, allClear, sections, summary } = model
    const domId = id => `${idPrefix}-${id}`
    const isOpen = s => (openState[s.id] ?? s.open)
    const toggle = s => setOpenState(prev => ({ ...prev, [s.id]: !isOpen(s) }))
    const jumpTo = id => {
        setOpenState(prev => ({ ...prev, [id]: true }))
        setTimeout(() => document.getElementById(domId(id))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0)
    }

    const sectionSummary = (s) => {
        if (s.id === 'watchlists') return <Badge tone='declined' dot>{`${hits.length} possible OFAC ${hits.length === 1 ? 'match' : 'matches'}`}</Badge>
        if (s.id === 'business') {
            return (
                <span className={styles.headerScore}>
                    <ScoreBar score={business.score} tone={business.scoreTone} size='sm' />
                    <span className={styles[`ink_${business.scoreTone}`]}>{scoreText(business.score)}</span>
                    <span className={shared.cardSummary}>{business.verifiedCount} of 7 details verified</span>
                </span>
            )
        }
        const o = s.owner
        return (
            <span className={styles.headerScore}>
                {o.sentAs && <span className={shared.cardSummary}>Sent as {o.sentAs}</span>}
                <ScoreBar score={o.score} tone={o.tone} capped={o.capped} size='sm' />
                <span className={styles[`ink_${o.tone}`]}>{scoreText(o.score)}</span>
            </span>
        )
    }

    const body = (s) => {
        if (s.id === 'watchlists') return <WatchlistHits hits={hits} />
        if (s.id === 'business') return <BusinessCheck business={business} />
        return <OwnerCard owner={s.owner} />
    }

    return (
        <div className={shared.body}>
            <div className={shared.summary}>
                <div className={shared.identity}>
                    <h2 className={shared.companyName}>{business.name}</h2>
                    {business.sentAs && <div><span className={styles.sentAs}>Sent as {business.sentAs}</span></div>}
                    <div className={shared.summaryLine}>{[business.address, business.phone, business.ein && `EIN ${business.ein}`].filter(Boolean).join(' · ')}</div>
                    <div className={shared.summaryLine}>{[business.addressType, business.mailDrop && 'Mail drop address', summary.ownersLine].filter(Boolean).join(' · ')}</div>
                    {verdict && <div className={shared.summaryBadges}><Badge tone={verdict.tone} dot>{verdict.text}</Badge></div>}
                </div>
                <ScoreDial score={business.score} tone={business.scoreTone} />
                <div className={cx(shared.tiles, styles.tiles)}>
                    {tiles.map(t => (
                        <button key={t.id} type='button' className={cx(shared.tile, shared.tileButton, TILE_CLASS[t.tone])} onClick={() => jumpTo(t.target)}>
                            <span className={shared.tileLabel}>{t.label}</span>
                            <span className={cx(shared.tileValue, shared.tileValueText, styles.tileValue)}>{t.value}</span>
                            {t.owners && t.owners.length > 0 && (
                                <span className={styles.tileBars}>
                                    {t.owners.map((o, i) => (
                                        <span key={i} className={styles.tileBar}>
                                            <ScoreBar score={o.score} tone={o.tone} capped={o.capped} size='xs' dark={!TILE_CLASS[t.tone]} />
                                            <span className={styles.tileBarName}>{o.name}</span>
                                        </span>
                                    ))}
                                </span>
                            )}
                            {t.sub && <span className={shared.tileSub}>{t.sub}</span>}
                        </button>
                    ))}
                </div>
            </div>

            {owners.length > 0 && (
                <div className={styles.owners}>
                    <div className={styles.ownersTitle}>Owners</div>
                    {owners.map(o => (
                        <button key={o.key} type='button' className={styles.ownerRow} onClick={() => jumpTo(o.key)}>
                            <ScoreBar score={o.score} tone={o.tone} capped={o.capped} size='sm' />
                            <span className={styles.ownerMain}>
                                <span className={styles.ownerName}>{o.name}</span>
                                <span className={styles.ownerSub}>
                                    <span className={styles[`ink_${o.tone}`]}>{scoreText(o.score)}</span>
                                    {o.link && ` · ${o.link.text}`}
                                </span>
                            </span>
                            <span className={shared.inlineBadges}>
                                {o.flags.filter(f => f.tone !== 'neutral').map(f => <Badge key={f.label} tone={badgeTone(f.tone)} dot={f.tone === 'declined'}>{f.label}</Badge>)}
                            </span>
                        </button>
                    ))}
                </div>
            )}

            {sections.length > 2 && (
                <nav className={shared.links} aria-label='Report sections'>
                    {sections.map(s => (
                        <button key={s.id} type='button' className={shared.link} onClick={() => jumpTo(s.id)}>
                            <span>{s.linkTitle}</span>
                            {s.count != null && (s.tone ? <Badge tone={s.tone}>{s.count}</Badge> : <span className={shared.linkCount}>{s.count}</span>)}
                        </button>
                    ))}
                </nav>
            )}

            {allClear && (
                <div className={shared.allClear} role='status'>
                    <VerifiedUserIcon className={shared.allClearIcon} />
                    <div><strong>Nothing to flag.</strong> Lexis verified the business{owners.length ? ' and its owners' : ''} with no watchlist hits.</div>
                </div>
            )}

            {sections.map(s => (
                <Section key={s.id} id={domId(s.id)} title={s.title} open={isOpen(s)} onToggle={() => toggle(s)} summary={sectionSummary(s)}>
                    {body(s)}
                </Section>
            ))}
        </div>
    )
}

export default withReportErrorBoundary(VerificationReport, 'result')
